import { inject, observer } from 'mobx-react';
import React, { Component } from 'react';
import { Button, Card, Flex, Heading, Text, Tooltip } from 'core/components';
import { CELL_TYPES, VirtualizedTable } from 'core/components/table';
import DeviceLink from 'app/components/links/DeviceLink';
import NmsInterfaceLink from 'app/components/links/NmsInterfaceLink';
import { ReactComponent as ManualOverrideIcon } from 'app/assets/icons/manual-override-icon.svg';
import InterfaceClassificationKv from 'app/components/interfaces/InterfaceClassificationKv';
import { processTopologyDataForDevice } from 'app/stores/metrics/metricsApi';
import ConnectionDialog from './ConnectionDialog';

@inject('$hybridMap', '$auth')
@observer
class MetricsConnections extends Component {
  static defaultProps = {
    showTitleBar: true,
    showLocalInterfaceColumn: true
  };

  state = {
    isDialogOpen: false,
    isRefreshing: false,
    selectedConnection: null
  };

  get columns() {
    const { showLocalInterfaceColumn } = this.props;

    const columns = [
      {
        label: 'Remote End',
        id: 'remote_end',
        renderer: ({ model }) => {
          const device_remote = model.get('device_remote');
          const device_remote_metadata = model.get('device_remote_metadata');
          const remote_display_name = model.get('remote_display_name');
          const interface_remote = model.get('interface_remote');
          const interface_remote_metadata = model.get('interface_remote_metadata');
          const interface_local_metadata = model.get('interface_local_metadata');

          if (remote_display_name) {
            return (
              <Flex alignItems="center" gap="4px">
                <Text>{remote_display_name} (External Link)</Text>
              </Flex>
            );
          }

          // For some connections, we can't determine the remote device/interface and just have the local interface
          if (!device_remote || !interface_remote) {
            const { lldp_rem_sys_name, lldp_rem_port_id } = interface_local_metadata || {};

            if (!lldp_rem_sys_name || !lldp_rem_port_id) {
              return (
                <Flex alignItems="center" gap="4px">
                  <Text>N/A</Text>
                </Flex>
              );
            }
            return (
              <Tooltip content={`${lldp_rem_sys_name}, ${lldp_rem_port_id}`}>
                <Flex gap="4px" flexDirection="column" tabIndex={0}>
                  <Text>{lldp_rem_sys_name}</Text>
                  <Text>{lldp_rem_port_id}</Text>
                </Flex>
              </Tooltip>
            );
          }

          return (
            <Flex gap="4px" flexDirection="column">
              {model.hasOverrides && showLocalInterfaceColumn !== true && (
                <ManualOverrideIcon style={{ minWidth: 16, minHeight: 16 }} />
              )}
              <DeviceLink deviceId={device_remote} name={device_remote_metadata?.device_name} />
              <NmsInterfaceLink deviceId={device_remote} snmpId={interface_remote}>
                {interface_remote_metadata.interface_description}
              </NmsInterfaceLink>
            </Flex>
          );
        }
      },
      {
        label: 'Classification',
        id: 'connectivity_type',
        renderer: ({ model }) => {
          const interface_remote_metadata = model.get('interface_remote_metadata');
          const interface_local_metadata = model.get('interface_local_metadata');

          const networkBoundary =
            interface_remote_metadata.network_boundary || interface_local_metadata.network_boundary || 'N/A';
          const connectivity =
            interface_remote_metadata.connectivity_type || interface_local_metadata.connectivity_type || 'N/A';

          return (
            <InterfaceClassificationKv
              networkBoundary={networkBoundary}
              connectivity={connectivity}
              showLinks={false}
              withTooltip
            />
          );
        }
      },
      {
        type: CELL_TYPES.ACTION,
        actions: [
          (model) => {
            if (!model.hasOverrides) {
              return null;
            }

            return (
              <Button
                key="edit"
                icon="edit"
                tracker="edit-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  this.handleSelectConnection(model);
                }}
                title="Edit"
                minimal
                small
              />
            );
          }
        ]
      }
    ];

    if (showLocalInterfaceColumn) {
      columns.unshift({
        label: 'Local Interface',
        name: 'interface_local_metadata.interface_description',
        renderer: ({ model }) => {
          const device_local = model.get('device_local');
          const interface_local = model.get('interface_local');
          const interface_local_metadata = model.get('interface_local_metadata');
          const interface_remote_metadata = model.get('interface_remote_metadata');

          if (!device_local || !interface_local) {
            const { lldp_rem_port_id } = interface_remote_metadata || {};
            return lldp_rem_port_id || 'N/A';
          }
          const snmpAlias = interface_local_metadata?.snmp_alias;

          return (
            <Flex justifyContent="center" gap="4px" flexDirection="column">
              <Flex alignItems="center" gap="4px">
                {model.hasOverrides && <ManualOverrideIcon style={{ minWidth: 16, minHeight: 16 }} />}
                <NmsInterfaceLink deviceId={device_local} snmpId={interface_local}>
                  {interface_local_metadata.interface_description}
                </NmsInterfaceLink>
              </Flex>
              {snmpAlias && (
                <Tooltip fill content={snmpAlias}>
                  <Text as="div" small muted ellipsis tabIndex={0}>
                    {snmpAlias}
                  </Text>
                </Tooltip>
              )}
            </Flex>
          );
        }
      });
    }

    return columns;
  }

  handleAddConnection = () => {
    const { collection } = this.props;
    this.setState({ selectedConnection: collection.forge() });
    this.handleToggleDialog();
  };

  handleSelectConnection = (connection) => {
    this.setState({ selectedConnection: connection });
    this.handleToggleDialog();
  };

  handleToggleDialog = () => {
    this.setState((prevState) => ({ isDialogOpen: !prevState.isDialogOpen }));
  };

  refreshTopology = () => {
    const { $hybridMap, device } = this.props;
    this.setState({ isRefreshing: true });

    $hybridMap.getTopologyDataForDevice(device.id, true).then((topology) => {
      processTopologyDataForDevice(device, topology);
      this.setState({ isRefreshing: false });
    });
  };

  render() {
    const { $auth, collection, showTitleBar, device, ...rest } = this.props;
    const { isDialogOpen, isRefreshing, selectedConnection } = this.state;

    if (!collection) {
      return null;
    }

    return (
      <>
        <Card
          overflow="hidden"
          position="relative"
          display="flex"
          flexDirection="column"
          elevation={0}
          flex={1}
          minHeight={250}
          {...rest}
        >
          {showTitleBar && (
            <Flex justifyContent="space-between" alignItems="center" py={1} px={2} minHeight={38} borderBottom="thin">
              <Heading level={6} m={0}>
                Connections
              </Heading>

              <Flex alignItems="center" gap={1}>
                {($auth.hasSudo || $auth.isSpoofed) && (
                  <Button minimal small icon="refresh" loading={isRefreshing} onClick={this.refreshTopology} />
                )}
                <Button
                  minimal
                  intent="primary"
                  icon="plus"
                  small
                  fontWeight="medium"
                  onClick={this.handleAddConnection}
                >
                  Add Connection
                </Button>
              </Flex>
            </Flex>
          )}

          <Flex flexDirection="column" flex="1 1 auto">
            <VirtualizedTable
              flexed
              columns={this.columns}
              collection={collection}
              selectOnRowClick={false}
              emptyState={
                <Text as="div" textAlign="center" muted small>
                  No connections found
                </Text>
              }
            />
          </Flex>
        </Card>
        <ConnectionDialog
          isOpen={isDialogOpen}
          onClose={this.handleToggleDialog}
          onRefreshTopology={this.refreshTopology}
          collection={collection}
          model={selectedConnection}
          device={device}
        />
      </>
    );
  }
}

export default MetricsConnections;
